import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { UploadZone } from "@/components/UploadZone";
import { WorkflowGraphic } from "@/components/WorkflowGraphic";
import { HomeClipAssistant } from "@/components/HomeClipAssistant";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import {
  Scissors, Captions, Wand2, Timer, Check,
} from "lucide-react";

const features = [
  { icon: Wand2, title: "AI highlight detection", desc: "Our model watches your full video and surfaces the moments most likely to go viral." },
  { icon: Scissors, title: "Auto vertical reframe", desc: "Every clip is rendered in crisp 9:16 with a cinematic blurred backdrop — post-ready." },
  { icon: Captions, title: "Burned-in captions", desc: "Word-perfect animated captions styled to your template. No editing apps needed." },
  { icon: Timer, title: "Save hours per video", desc: "Turn one long-form upload into a batch of shorts in a single click." },
];

export function Landing() {
  const navigate = useNavigate();
  const { user, openAuth } = useAuth();

  return (
    <div data-testid="landing-page" className="landing-page relative">
      {/* UPLOAD-FIRST HERO */}
      <section id="upload" data-testid="landing-upload-section" aria-labelledby="landing-upload-title" className="landing-gutter pt-28 sm:pt-36 pb-16 scroll-mt-20">
        <div className="w-full min-w-0">
          <div className="max-w-4xl mx-auto text-center mb-8">
            <h1 id="landing-upload-title" data-testid="landing-upload-heading" className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">Viral clips in a few clicks.</h1>
          </div>
          <HomeClipAssistant />
          <div data-testid="landing-upload-card" className="landing-upload-card">
            <UploadZone onUploaded={(id) => navigate(`/processing/${id}`)} afterDropzone={<WorkflowGraphic />} />
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" data-testid="landing-features-section" className="landing-gutter py-20 scroll-mt-20">
        <div data-testid="landing-features-content" className="w-full min-w-0">
          <div className="max-w-2xl mb-12">
            <p className="text-xs uppercase tracking-[0.25em] text-copy-500 mb-3">Everything, automated</p>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter">
              Post-ready clips without touching an editor.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((f, i) => (
              <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="rounded-2xl border border-surface-800 bg-panel p-6 hover:-translate-y-1 hover:border-surface-700 transition-[transform,border-color] duration-300">
                <div className="h-11 w-11 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-4">
                  <f.icon className="h-5 w-5 text-cyan-400" />
                </div>
                <h3 className="font-display font-semibold text-lg">{f.title}</h3>
                <p className="max-w-prose text-sm text-copy-500 mt-2 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" data-testid="landing-how-section" className="landing-gutter py-20 scroll-mt-20">
        <div data-testid="landing-how-content" className="w-full min-w-0">
          <div className="min-w-0">
            <p data-testid="landing-how-label" className="text-xs uppercase tracking-[0.25em] text-copy-500 mb-3">How it works</p>
            <h2 data-testid="landing-how-heading" className="font-display text-4xl font-bold tracking-tighter mb-8">Three steps. Zero editing.</h2>
            <div data-testid="landing-how-steps" className="grid gap-6 md:grid-cols-3">
              {[
                { n: "01", t: "Upload your long-form video", d: "Podcast, interview, stream or vlog — drop the raw file." },
                { n: "02", t: "AI finds & cuts the highlights", d: "It transcribes, scores virality, and reframes to vertical with captions." },
                { n: "03", t: "Download & post everywhere", d: "Grab your batch of clips for TikTok, Reels, Shorts and Facebook." },
              ].map((s) => (
                <div key={s.n} data-testid={`landing-how-step-${s.n}`} className="flex min-w-0 gap-5">
                  <span data-testid={`landing-how-step-number-${s.n}`} className="font-display text-2xl font-black text-gradient">{s.n}</span>
                  <div className="min-w-0">
                    <h3 data-testid={`landing-how-step-title-${s.n}`} className="font-display font-semibold text-lg">{s.t}</h3>
                    <p data-testid={`landing-how-step-description-${s.n}`} className="text-sm text-copy-500 mt-1">{s.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" data-testid="landing-pricing-section" className="landing-gutter py-20 scroll-mt-20">
        <div data-testid="landing-pricing-content" className="w-full min-w-0">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <p className="text-xs uppercase tracking-[0.25em] text-copy-500 mb-3">Pricing</p>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter">Start free. Scale when you're ready.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="rounded-3xl border border-surface-800 bg-panel p-8">
              <h3 className="font-display text-xl font-bold">Free Trial</h3>
              <p className="text-4xl font-display font-black mt-3">$0</p>
              <p className="text-sm text-copy-500 mt-1">No account required</p>
              <ul className="space-y-3 mt-6 text-sm text-copy-300">
                {["10 clips total", "AI highlight detection", "Auto captions", "Watermarked exports"].map((x) => (
                  <li key={x} className="flex items-center gap-2"><Check className="h-4 w-4 text-cyan-400" /> {x}</li>
                ))}
              </ul>
              <Button data-testid="pricing-upload-btn" onClick={() => document.getElementById("upload")?.scrollIntoView({ behavior: "smooth" })}
                className="w-full mt-7 rounded-full bg-surface-100 text-black hover:bg-white font-semibold">Try it now</Button>
            </div>
            <div className="rounded-3xl border border-cyan-500/40 bg-gradient-to-b from-cyan-500/[0.06] to-transparent p-8 relative">
              <span className="absolute top-6 right-6 text-[10px] uppercase tracking-widest text-cyan-400 border border-cyan-500/40 rounded-full px-2 py-0.5">Popular</span>
              <h3 className="font-display text-xl font-bold">Creator</h3>
              <p className="text-4xl font-display font-black mt-3">Free account</p>
              <p className="text-sm text-copy-500 mt-1">Everything unlocked</p>
              <ul className="space-y-3 mt-6 text-sm text-copy-300">
                {["Up to 100 clips", "No watermark", "Custom templates", "Personal dashboard"].map((x) => (
                  <li key={x} className="flex items-center gap-2"><Check className="h-4 w-4 text-cyan-400" /> {x}</li>
                ))}
              </ul>
              <Button data-testid="pricing-signup-btn" onClick={() => (user ? navigate("/dashboard") : openAuth("register"))}
                className="w-full mt-7 rounded-full bg-cyan-500 text-black hover:bg-cyan-400 font-semibold">
                {user ? "Go to dashboard" : "Create free account"}
              </Button>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
